<template>
    <b-col>
        <b-form-row>
            <b-col cols="12">
                <EnumSelector v-model="overrides.submissionClarificationCode"
                    :enum-codes="submissionClarificationCodes" :label-cols="3"
                    label="Submission Clarification Code"></EnumSelector>
            </b-col>
        </b-form-row>
        <b-form-row>
            <b-col cols="12">
                <EnumSelector v-model="overrides.eligibilityClarificationCodeOverride"
                    :enum-codes="eligibilityClarificationCodes"
                    :elements-to-discard="discardedEligibilityClarificationCodes" :label-cols="3"
                    label="Eligibility Clarification Code" clearable></EnumSelector>
            </b-col>
        </b-form-row>
        <b-form-row>
            <b-col cols="4">
                <EnumSelector v-model="overrides.paTypeCode" :enum-codes="paTypeCodes" :label-cols="4" label="PA Type">
                </EnumSelector>
            </b-col>
            <b-col cols="4">
                <!-- maxlength on the free-text overrides is the NCPDP field width the value is
                     transmitted in. The request builder truncates anything longer without saying so, so
                     without these a technician loses characters silently rather than being stopped. -->
                <b-form-group label="PA#:" label-cols="4" :label-for="fieldId('pa-override')">
                    <b-form-input :id="fieldId('pa-override')" v-model="overrides.paOverride"
                        maxlength="11"></b-form-input>
                </b-form-group>
            </b-col>
            <b-col cols="4">
                <b-form-group label="TSN:" label-cols="4" :label-for="fieldId('tsn')">
                    <b-form-input :id="fieldId('tsn')" v-model="overrides.tsn" maxlength="12"></b-form-input>
                </b-form-group>
            </b-col>
        </b-form-row>
        <!-- Two per row rather than three: at a third of the width these two labels wrap onto a
             second line, leaving a single-line input floating against a two-line label. -->
        <b-form-row>
            <b-col cols="6">
                <b-form-group label="Usual And Customary Override:" label-cols="6"
                    :label-for="fieldId('uac-override')">
                    <b-form-input :id="fieldId('uac-override')" v-model="overrides.uacOverride" min="0.00" step="0.01"
                        type="number" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group label="Incentive Amount Override:" label-cols="6"
                    :label-for="fieldId('incentive-amount-override')">
                    <b-form-input :id="fieldId('incentive-amount-override')" v-model="overrides.incentiveAmountOverride"
                        min="0.00" step="0.01" type="number" />
                </b-form-group>
            </b-col>
        </b-form-row>
        <b-form-row>
            <b-col cols="6">
                <b-form-group label="ICD-10:" label-cols="6" :label-for="fieldId('icd-10')">
                    <b-form-input :id="fieldId('icd-10')" v-model="overrides.icd10" maxlength="15" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group label="Date of Injury:" label-cols="6"
                    :label-for="fieldId('date-of-injury-override')">
                    <b-form-input :id="fieldId('date-of-injury-override')" v-model="overrides.dateOfInjuryOverride"
                        type="date" />
                </b-form-group>
            </b-col>
        </b-form-row>
        <b-form-row v-if="showOtherCoverageCodeOverride || showTaxPercentageOverride">
            <b-col v-if="showOtherCoverageCodeOverride" cols="6">
                <EnumSelector v-model="overrides.otherCoverageCodeOverride" :enum-codes="otherCoverageCodes"
                    :label-cols="6" label="Other Coverage Code Override" clearable />
            </b-col>
            <b-col v-if="showTaxPercentageOverride" cols="6">
                <b-form-group label="Tax Percentage Override (%):" label-cols="6"
                    :label-for="fieldId('tax-percentage-override')">
                    <b-form-input :id="fieldId('tax-percentage-override')"
                        v-model.number="overrides.taxPercentageOverride" min="0" max="100" step="0.001"
                        type="number" placeholder="e.g. 8.25" />
                </b-form-group>
            </b-col>
        </b-form-row>
        <b-form-row v-if="showRejectCodeOverride">
            <b-col cols="6">
                <b-form-group label="Reject Code Override:" label-cols="6" :label-for="fieldId('reject-code-override')"
                    :description="rejectCodeDescription">
                    <b-form-select :id="fieldId('reject-code-override')" v-model="overrides.rejectCodeOverride"
                        :disabled="rejectCodeOptions.length === 0">
                        <template #first>
                            <b-form-select-option :value="null">
                                {{ rejectCodeOptions.length === 0 ? noRejectCodesText : 'Default (lowest code)' }}
                            </b-form-select-option>
                        </template>
                        <b-form-select-option v-for="opt in rejectCodeOptions" :key="opt.value" :value="opt.value">
                            {{ opt.text }}
                        </b-form-select-option>
                    </b-form-select>
                </b-form-group>
            </b-col>
        </b-form-row>
        <b-form-row v-if="hasAdminPrescriptionPermission">
            <b-col cols="6">
                <b-form-group label="Days Supply Override:" label-cols="6" :label-for="fieldId('days-supply-override')">
                    <b-form-input :id="fieldId('days-supply-override')" v-model.number="overrides.daysSupplyOverride"
                        min="1" type="number" placeholder="Override days supply" />
                </b-form-group>
            </b-col>
        </b-form-row>
        <b-row>
            <b-col>
                <b-button @click="addDur">Add Dur Pps Override</b-button>
            </b-col>
        </b-row>
        <!-- Capped at the two-character NCPDP code width for the same reason as the boxes above, and one
             more: a scheduled claim stores these as typed, into two-character columns, so an uncapped
             entry did not lose characters quietly but failed the whole save as a bare database error. -->
        <b-form-row v-for="(dur, index) in overrides.durPpsOverrides" :key="index">
            <b-col cols="8">
                <b-form-group label="Dur Pps Override:" label-cols="3">
                    <b-form-group :label-for="fieldId(`dur-service-reason-${index}`)" class="mb-1"
                        label="Reason For Service:" label-cols="4">
                        <b-form-input :id="fieldId(`dur-service-reason-${index}`)" v-model="dur.reasonForServiceCode"
                            placeholder="Reason For Service Code" maxlength="2"></b-form-input>
                    </b-form-group>
                    <b-form-group :label-for="fieldId(`dur-professional-service-${index}`)" class="mb-1"
                        label="Professional Service:" label-cols="4">
                        <b-form-input :id="fieldId(`dur-professional-service-${index}`)"
                            v-model="dur.professionalServiceCode"
                            placeholder="Professional Service Code" maxlength="2"></b-form-input>
                    </b-form-group>
                    <b-form-group :label-for="fieldId(`dur-service-result-${index}`)" class="mb-1"
                        label="Result Of Service:" label-cols="4">
                        <b-form-input :id="fieldId(`dur-service-result-${index}`)" v-model="dur.resultOfServiceCode"
                            placeholder="Result Of Service Code" maxlength="2"></b-form-input>
                    </b-form-group>
                    <b-form-group :label-for="fieldId(`dur-effort-${index}`)" class="mb-1" label="Level Of Effort:"
                        label-cols="4">
                        <b-form-input :id="fieldId(`dur-effort-${index}`)" v-model="dur.levelOfEffort"
                            placeholder="Level Of Effort" maxlength="2"></b-form-input>
                    </b-form-group>
                </b-form-group>
            </b-col>
            <b-col cols="4">
                <b-button :id="fieldId(`dur-remove-${index}`)" size="sm" variant="outline-danger"
                    @click="removeDur(index)">Remove
                </b-button>
            </b-col>
        </b-form-row>
    </b-col>
</template>

<script lang="ts">
import { Component, Prop, Vue, VModel } from 'vue-property-decorator';
import EnumSelector from '@/components/EnumSelector.vue';
import { BillingOverrides } from '@/models/BillingOverrides';
import {
    EligibilityClarificationCode,
    OtherCoverageCode,
    PriorAuthorizationTypeCode,
    SubmissionClarificationCode,
} from '@/models/PatientPlan';

/**
 * The per-claim NCPDP override fields, shared by the immediate billing path and by claim
 * scheduling. Both paths transmit through the same billing call, so they have to offer the same
 * fields with the same permission gating — keeping one form is what stops the two from drifting.
 *
 * The permission checks live here rather than being passed in: they gate which fields exist at
 * all, which is a property of the fields, not of the screen showing them.
 */
@Component({
    name: "BillingOverridesForm",
    components: { EnumSelector },
})
export default class BillingOverridesForm extends Vue {
    @VModel() private overrides!: BillingOverrides;

    /**
     * Reject codes the user may force into a secondary claim's COB segment. Supplied by the parent
     * because where they come from differs: an immediate bill reads them off the prescription's
     * most recent rejection, while a scheduled one can only read them off what an earlier plan in
     * the staged sequence is expected to return.
     */
    @Prop({ default: () => [] }) private rejectCodeOptions!: { value: string, text: string; }[];

    /** Shown in place of a code list when there are none to offer; the reason differs by caller. */
    @Prop({ default: 'No reject codes on primary claim' }) private noRejectCodesText!: string;

    @Prop({ default: 'Forces which primary-claim reject code is sent in the secondary claim\'s COB segment.' })
    private rejectCodeDescription!: string;

    /**
     * Both callers can be on the page at once, so element ids are namespaced per instance —
     * duplicate ids would point every label at whichever field rendered first.
     */
    @Prop({ default: 'billing' }) private idPrefix!: string;

    // noinspection JSUnusedLocalSymbols
    private paTypeCodes = PriorAuthorizationTypeCode;
    // noinspection JSUnusedLocalSymbols
    private submissionClarificationCodes = SubmissionClarificationCode;
    // noinspection JSUnusedLocalSymbols
    private otherCoverageCodes = OtherCoverageCode;
    // noinspection JSUnusedLocalSymbols
    private eligibilityClarificationCodes = EligibilityClarificationCode;
    // Clearing the selector is how "no override" is expressed; an explicit 0 would be transmitted.
    // noinspection JSUnusedLocalSymbols
    private discardedEligibilityClarificationCodes = [EligibilityClarificationCode.NotSpecified];

    fieldId(name: string): string {
        return `${this.idPrefix}-${name}`;
    }

    get showOtherCoverageCodeOverride(): boolean {
        return this.$permissions.some(p => p.permissionCodeName == "Patient Dashboard/Override Other Coverage Code");
    }

    get showTaxPercentageOverride(): boolean {
        return this.$permissions.some(p => p.permissionCodeName == "Patient Dashboard/Override Tax Percentage");
    }

    get showRejectCodeOverride(): boolean {
        return this.$permissions.some(p => p.permissionCodeName == "Patient Dashboard/Override Reject Code");
    }

    get hasAdminPrescriptionPermission(): boolean {
        return this.$permissions.some(p => p.permissionCodeName == "Admin/Prescription");
    }

    addDur() {
        this.overrides.durPpsOverrides.push(BillingOverrides.emptyDur());
    }

    /**
     * Drops one DUR/PPS row. Added because a row could only ever be blanked out, not withdrawn — the
     * four codes are transmitted as a set, so a half-cleared row is not the same as no row, and
     * scheduling makes it likelier that a technician wants to undo one hours after adding it.
     *
     * Splices the bound array in place rather than reassigning it: v-model hands this form the
     * parent's own BillingOverrides, and replacing the array would leave the parent holding the old one.
     */
    removeDur(index: number) {
        this.overrides.durPpsOverrides.splice(index, 1);
    }
}
</script>
